import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useUserStore } from "@/store/useUserStore";
import { useWorkspaceStore } from "@/store/useWorkspaceStore";
import { getStaffConfig } from "@/api/staff";
import { fetchUserProfile } from "@/api/user";

/**
 * 微信授权路由守卫
 * 渲染子路由前检查：
 *   1. 是否已登录（token 是否存在）
 *   2. 进入 /workspace 时是否为达人（is_talent === 1）
 *   3. 已登录时预取 is_talent（首次加载也生效）
 */

interface Props {
  children: ReactNode;
}

/** 从 localStorage 直接读取 token（兜底 Zustand persist 未及时恢复的情况） */
function getLocalToken(): string | null {
  try {
    const raw = localStorage.getItem("user-storage");
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed.state?.token ?? parsed.token ?? null;
    }
  } catch { /* ignore */ }
  return null;
}

/** 全局单例：防止重复请求 profile */
let talentCheckPromise: Promise<void> | null = null;

export default function WxAuthGuard({ children }: Props) {
  const location = useLocation();
  const [checked, setChecked] = useState(false);

  // 登录状态
  const isLoggedIn = useUserStore((state) => state.isLoggedIn);
  const wechatUser = useUserStore((state) => state.wechatUser);
  const isTokenExpired = useUserStore((state) => state.isTokenExpired);

  // 达人身份
  const isTalent = useUserStore((state) => state.isTalent);
  const setIsTalent = useUserStore((state) => state.setIsTalent);

  // ── 工作台 config 预加载 ──
  const config = useWorkspaceStore((s) => s.config);
  const setConfig = useWorkspaceStore((s) => s.setConfig);

  // 延迟一帧确保 Zustand 已从 localStorage 恢复状态
  useEffect(() => {
    const timer = setTimeout(() => setChecked(true), 0);
    return () => clearTimeout(timer);
  }, []);

  // 登录判定
  const hasLocalToken = !isLoggedIn && !!getLocalToken();
  const isAuthenticated = isLoggedIn || hasLocalToken;

  // ── 已登录时自动预取 is_talent（首次加载即生效）──
  useEffect(() => {
    if (!checked) return;
    if (!isAuthenticated) return;
    // store 中已有 isTalent=1 则跳过
    if (isTalent === 1) return;

    // 全局单例：防止多组件/多次渲染重复请求
    if (!talentCheckPromise) {
      talentCheckPromise = fetchUserProfile()
        .then((data) => {
          // ★ 只信任接口返回值，非 1 一律按 0 处理（与 TalentPrefetch / 个人中心口径一致）
          const talent = (data.is_talent ?? data.user?.is_talent) === 1 ? 1 : 0;
          setIsTalent(talent);
          // 同步写入独立缓存，确保 UserBottomNav 在任何入口都能正确读取
          localStorage.setItem("is_talent_cache", String(talent));
        })
        .catch(() => {
          // ★ 接口失败 → 显式置 0（非达人），避免上个账号残留 isTalent=1 误显示工作台
          setIsTalent(0);
          localStorage.setItem("is_talent_cache", "0");
        })
        .finally(() => {
          // 延迟清除，允许短时间内复用结果
          setTimeout(() => { talentCheckPromise = null; }, 3000);
        });
    }
  }, [checked, isAuthenticated, isTalent, setIsTalent]);

  // 工作台 config 预加载
  useEffect(() => {
    if (!checked) return;
    if (!isAuthenticated) return;
    if (!location.pathname.startsWith("/workspace")) return;
    if (config) return;

    let alive = true;
    const g: any = globalThis as any;
    if (!g.__workspaceConfigPromise) {
      g.__workspaceConfigPromise = getStaffConfig()
        .then((c) => {
          if (alive) setConfig(c);
          return c;
        })
        .catch((e) => {
          throw e;
        })
        .finally(() => {
          g.__workspaceConfigPromise = null;
        });
    } else {
      g.__workspaceConfigPromise
        .then((c: any) => {
          if (alive && c && !config) setConfig(c);
        })
        .catch(() => { /* ignore */ });
    }
    return () => { alive = false };
  }, [checked, isAuthenticated, location.pathname]);

  // 首次渲染未完成检查时，不闪现内容
  if (!checked) return null;

  // ① 未登录 → 跳转微信授权
  if (!isAuthenticated) {
    return (
      <Navigate
        to={`/wx-auth?redirect=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  // ② 非达人访问工作台 → 拦截回首页（招募申请页除外）
  const isWorkspacePath = location.pathname.startsWith("/workspace");
  const isRecruitPath = location.pathname === "/workspace/recruit";
  if (isWorkspacePath && !isRecruitPath && isTalent !== 1) {
    return (
      <Navigate to="/ai-voice" replace />
    );
  }

  return <>{children}</>;
}
